import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { tokens } from '../../theme';
import { authService, reportService, userService } from '../../services/api';
import { useToast } from '../../context/ToastContext';

// ─────────────────────────────────────────────────────────────────────────────
// AdminDashboard — package KPI + quick-actions design, real wiring underneath.
//
// VISUAL: package design — period filter, compliance alert strip, 3 KPI cards,
//   quick-action cards below. All on design-system tokens.
// LOGIC PRESERVED from the real AdminDashboard:
//   - Author-enablement flow on "Add content": existing authors go straight to
//     /contents; non-authors get UploadContentRoleModal to enable/switch role.
//   - Invite learners -> /users   ·   Add skills -> /skills
// PLACEHOLDER (backend does not yet return these — see QA seam SEAM-3):
//   - KPI values (activeUsers, seatsUsed/Total, searchCount, searchMatchRate,
//     complianceAlert). Rendered with a "Sample data" pill until the dashboard
//     API returns them. Falls back to 0/null safely.
// ─────────────────────────────────────────────────────────────────────────────

// ── Placeholder marker (shared convention) ────────────────────────────────────
const PlaceholderPill = () => (
    <Box
        component="span"
        sx={{
            fontSize: tokens.font.size.xs, fontWeight: tokens.font.weight.medium,
            color: tokens.color.textTertiary, background: tokens.color.bgTertiary,
            border: `0.5px solid ${tokens.color.borderTertiary}`,
            borderRadius: tokens.radius.sm, padding: '1px 7px', marginLeft: '8px',
            letterSpacing: '0.02em', verticalAlign: 'middle', fontFamily: tokens.font.family,
        }}
    >
        Sample data
    </Box>
);

const PeriodPill = ({ options, active, onChange }) => (
    <Box sx={{ display: 'flex', gap: '6px' }}>
        {options.map(opt => (
            <Box
                key={opt.value}
                onClick={() => onChange(opt.value)}
                sx={{
                    fontSize: tokens.font.size.xs,
                    color: active === opt.value ? tokens.color.textPrimary : tokens.color.textSecondary,
                    backgroundColor: active === opt.value ? tokens.color.bgSecondary : 'transparent',
                    border: `0.5px solid ${active === opt.value ? tokens.color.borderSecondary : tokens.color.borderTertiary}`,
                    borderRadius: tokens.radius.pill, padding: '4px 13px', cursor: 'pointer',
                    fontFamily: tokens.font.family, transition: `all ${tokens.transition}`, userSelect: 'none',
                }}
            >
                {opt.label}
            </Box>
        ))}
    </Box>
);

const AlertStrip = ({ alert, onDismiss }) => {
    const navigate = useNavigate();
    if (!alert || !alert.count) return null;
    const handleViewOverdue = () => {
        navigate('/reports/compliance');
    };
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: tokens.color.bgPrimary, border: `0.5px solid ${tokens.color.alertBorder}`, borderRadius: tokens.radius.md, padding: '12px 18px', marginBottom: '28px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0,}}>
                <Box sx={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: tokens.color.alertDot, flexShrink: 0 }} />
                <Typography sx={{ fontSize: tokens.font.size.md, color: tokens.color.textPrimary, fontFamily: tokens.font.family }}>
                    {alert.count} learners have compliance training due in {alert.daysUntilDue} days
                    {' '}
                    <Box component="span" sx={{ color: tokens.color.textSecondary }}>· {alert.audience}</Box>
                </Typography>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginLeft: '16px',
                    flexShrink: 0,
                }}
            >
                {/* View overdue */}
                <Box
                    onClick={handleViewOverdue}
                    sx={{
                        fontSize: tokens.font.size.sm,
                        color: tokens.color.textSecondary,
                        cursor: 'pointer',
                        fontFamily: tokens.font.family,
                        whiteSpace: 'nowrap',
                        '&:hover': {
                            color: tokens.color.textPrimary,
                            textDecoration: 'underline',
                        },
                    }}
                >
                    View overdue
                </Box>

                {/* Close */}
                <Box
                    onClick={onDismiss}
                    role="button"
                    aria-label="Dismiss alert"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        fontSize: '20px',
                        lineHeight: 1,
                        color: tokens.color.textSecondary,
                        cursor: 'pointer',
                        borderRadius: '4px',
                        '&:hover': {
                            color: tokens.color.textPrimary,
                            backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        },
                    }}
                >
                    ×
                </Box>
            </Box>
        </Box>
    );
};

// note: a second line under sub, in the sub's quiet colour (the widget share under Searches, K6.1 pt 3b).
const KPICard = ({ label, value, sub, subType, note, placeholder }) => (
    <Box sx={{ backgroundColor: tokens.color.bgPrimary, border: `0.5px solid ${tokens.color.borderTertiary}`, borderRadius: tokens.radius.md, padding: '20px 22px' }}>
        <Typography sx={{ fontSize: tokens.font.size.xs, color: tokens.color.textSecondary, letterSpacing: tokens.font.letterSpacing.label, textTransform: 'uppercase', marginBottom: '10px', fontFamily: tokens.font.family }}>
            {label}{placeholder && <PlaceholderPill />}
        </Typography>
        <Typography sx={{ fontSize: '36px', fontWeight: tokens.font.weight.medium, color: tokens.color.textPrimary, letterSpacing: '-0.03em', lineHeight: 1, marginBottom: '7px', fontFamily: tokens.font.family }}>
            {value}
        </Typography>
        {sub && (
            <Typography sx={{ fontSize: tokens.font.size.sm, color: subType === 'positive' ? tokens.color.positive : subType === 'warning' ? tokens.color.warning : tokens.color.textSecondary, fontFamily: tokens.font.family }}>
                {sub}
            </Typography>
        )}
        {note && (
            <Typography sx={{ fontSize: tokens.font.size.sm, color: tokens.color.textSecondary, fontFamily: tokens.font.family, marginTop: '2px' }}>
                {note}
            </Typography>
        )}
    </Box>
);

const ActionCard = ({ title, desc, onClick }) => (
    <Box
        onClick={onClick}
        sx={{ backgroundColor: tokens.color.bgPrimary, border: `0.5px solid ${tokens.color.borderTertiary}`, borderRadius: tokens.radius.lg, padding: '22px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', transition: `border-color ${tokens.transition}, background ${tokens.transition}`, '&:hover': { borderColor: tokens.color.borderPrimary, backgroundColor: tokens.color.bgSecondary } }}
    >
        <Box>
            <Typography sx={{ fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium, color: tokens.color.textPrimary, marginBottom: '4px', fontFamily: tokens.font.family }}>
                {title}
            </Typography>
            <Typography sx={{ fontSize: tokens.font.size.sm, color: tokens.color.textSecondary, fontFamily: tokens.font.family }}>
                {desc}
            </Typography>
        </Box>
        <Box sx={{ fontSize: '18px', color: tokens.color.textSecondary, marginLeft: '16px', flexShrink: 0 }}>→</Box>
    </Box>
);

const PERIOD_OPTIONS = [
    { label: '7 days', value: '7d' },
    { label: '30 days', value: '30d' },
    { label: 'All time', value: 'all' },
];

const AdminDashboard = ({ data }) => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { user, dashboardData } = data || {};

    const [period, setPeriod] = useState('7d');
    const [alertDismissed, setAlertDismissed] = useState(false);

    // Author-enablement modal state (preserved from real AdminDashboard)
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // ── KPI values — fetch live report stats and search stats and map fields ──
    // We prefer the report endpoints; if fields are missing we gracefully
    // degrade to the `dashboardData` prop or fallbacks.
    const [stats, setStats] = useState(null);
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const [dashResp] = await Promise.all([
                    reportService.getDashboardStats({ period })
                ]);

                const dash = dashResp?.data?.success ? dashResp.data.data : dashResp?.data ?? null;
                //const search = searchResp?.data?.success ? searchResp.data.data : searchResp?.data ?? null;

                const mapped = {
                    period,
                    // active users: API returns `activeUsers`
                    activeUsers: dash?.activeUsers ?? dash?.activeLearners ?? null,
                    // seats total (licenses)
                    seatsTotal: dash?.seatsTotal ?? dash?.max_trial_users ?? null,
                    // growth information from server
                    activePrev: dash?.activePrev ?? null,
                    activeDelta: dash?.activeDelta ?? null,
                    activePercentChange: dash?.activePercentChange ?? null,
                    // derive a usable search count from topSearches if available
                    searchCount: dash?.searchCount ?? null,
                    // overall match percentage if available
                    searchMatchRate: dash?.searchMatchRate ?? null,
                    // the widget's share of those searches, null when the line is not shown (K6.1 pt 3b)
                    widgetShare: dash?.widgetShare ?? null,
                    widgetSearches: dash?.widgetSearches ?? 0,
                    complianceAlert: dash?.complianceAlert ?? null
                };

                if (mounted) setStats(mapped);
            } catch (e) {
                if (mounted) setStats(null);
            }
        })();
        return () => { mounted = false; };
    }, [period]);

    // Prefer live stats; fall back to any dashboardData passed via props.
    const kpi = stats || dashboardData || null;
    //const hasKpiData = !!kpi && (kpi.activeUsers != null || kpi.searchCount != null || kpi.seatsTotal != null);
    const hasKpiData = !!kpi && (kpi.activeUsers != null || kpi.searchCount != null);
    const activeUsers = kpi?.activeUsers ?? 0;
    const seatsUsed = kpi?.seatsUsed ?? 0;
    const seatsTotal = kpi?.seatsTotal ?? null;
    const searchCount = kpi?.searchCount ?? 0;
    const searchMatchRate = kpi?.searchMatchRate ?? null;
    const complianceAlert = kpi?.complianceAlert ?? null;

    // Header
    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
    const firstName = user?.name?.split(' ')[0] || 'there';
    const workspaceName = user?.workspace_name || user?.account_name || '';
    const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

    //const seatsSubValue = seatsTotal ? `${seatsTotal - activeUsers} seats available` : 'No cap set';
    const matchRateNote = searchMatchRate !== null ? `${searchMatchRate}% match rate` : null;
    // Under Searches, the widget's share in whole percent rounded down, 'Under 1%' when some searches came from it but
    // fewer than 1 in 100; only when the workspace has an active widget token and there were searches (K6.1 pt 3b,
    // K3 pt 15). The server sends null otherwise.
    const widgetShare = kpi?.widgetShare ?? null;
    const widgetNote = widgetShare === null ? null
        : `${widgetShare < 1 && (kpi?.widgetSearches ?? 0) > 0 ? 'Under 1%' : `${widgetShare}%`} via the widget`;
    /*
    const activeSubText = (kpi && (kpi.activePercentChange !== null && kpi.activePercentChange !== undefined))
        ? `${kpi.activePercentChange > 0 ? '+' : ''}${kpi.activePercentChange}% from previous period`
        : (kpi && kpi.activeDelta ? `${kpi.activeDelta} change` : '—');
    */
    // Sub-text under Active Users. "All time" has no previous period, so it gets a
    // plain descriptor. 7/30 days show the change vs the preceding window of the same length.
    const ACTIVE_DESCRIPTOR = 'unique active users';
    let activeSubText = ACTIVE_DESCRIPTOR;
    let activeSubType = 'default';
    if (!kpi || (kpi.period && kpi.period !== period)) {
        activeSubText = '—'; // numbers still loading for the newly selected period
    } else if (period !== 'all') {
        const windowLabel = period === '7d' ? 'previous 7 days' : 'previous 30 days';
        const rawDelta = kpi.activeDelta ?? (kpi.activePrev != null ? activeUsers - kpi.activePrev : null);
        const delta = rawDelta == null ? null : Number(rawDelta);
        const pct = kpi.activePercentChange == null ? null : Number(kpi.activePercentChange);
        if (delta != null && Number.isFinite(delta)) {
            activeSubText = delta === 0 ? `No change vs ${windowLabel}` : `${delta > 0 ? '+' : '-'}${Math.abs(delta)} vs ${windowLabel}`;
            activeSubType = delta > 0 ? 'positive' : delta < 0 ? 'warning' : 'default';
        } else if (pct != null && Number.isFinite(pct)) {
            activeSubText = pct === 0 ? `No change vs ${windowLabel}` : `${pct > 0 ? '+' : '-'}${Math.abs(pct)}% vs ${windowLabel}`;
            activeSubType = pct > 0 ? 'positive' : pct < 0 ? 'warning' : 'default';
        }
    }    
    const matchRateType = searchMatchRate !== null && searchMatchRate < 50 ? 'warning' : 'default';
    const handleActiveUsers = () => navigate('/reports/active-users');
    const handleInviteLearners = () => navigate('/users');
    const handleAddContent = async () => {
        setLoading(true);
        try {
            const currentUser = user || {};
            const isAuthor = (currentUser.role_name && currentUser.role_name.toLowerCase() === 'author')
                || (Array.isArray(currentUser.roles) && currentUser.roles.some(r => r.name && r.name.toLowerCase() === 'author'));

            // If already author, switch to the author role (if role id available) then redirect
            if (isAuthor) {
                let authorRoleId = null;
                if (Array.isArray(currentUser.roles)) {
                    const r = currentUser.roles.find(r => r.name && r.name.toLowerCase() === 'author');
                    if (r) authorRoleId = r.id;
                }
                if (!authorRoleId && currentUser.role_id && currentUser.role_name && currentUser.role_name.toLowerCase() === 'author') {
                    authorRoleId = currentUser.role_id;
                }

                if (authorRoleId) await authService.switchRole(authorRoleId);
                // navigate to contents area
                window.location.href = '/contents';
                return;
            }

            // Not an author: enable author role for the user, then switch and redirect
            const resp = await userService.enableAuthorRole();
            const authorRoleId = resp?.data?.authorRoleId || resp?.data?.roleId || null;

            if (authorRoleId) {
                await authService.switchRole(authorRoleId);
                window.location.href = '/contents';
                return;
            }

            // Fallback: if enable endpoint didn't return role id, try fetching current user's roles and switch
            try {
                const uid = currentUser.id;
                if (uid) {
                    const rolesResp = await userService.getUserRoles(uid);
                    const roles = rolesResp?.data?.data || rolesResp?.data || [];
                    const found = roles.find(r => r.name && r.name.toLowerCase() === 'author');
                    if (found && found.id) {
                        await authService.switchRole(found.id);
                    }
                }
            } catch (e) {
                // ignore and continue
            }

            // Finally redirect to contents regardless
            window.location.href = '/contents';
        } catch (e) {
            console.error('Navigation to contents failed:', e);
            showToast('Could not open the content area. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ width: '100%', py: '24px' }}>
            {/* Header + period filter */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '32px' }}>
                <Box>
                    <Typography sx={{ fontSize: '21px', fontWeight: tokens.font.weight.medium, color: tokens.color.textPrimary, letterSpacing: '-0.02em', marginBottom: '4px', fontFamily: tokens.font.family }}>
                        {greeting}, {firstName}
                    </Typography>
                    <Typography sx={{ fontSize: tokens.font.size.md, color: tokens.color.textSecondary, fontFamily: tokens.font.family }}>
                        {workspaceName && `${workspaceName} · `}{today}
                    </Typography>
                </Box>
                <PeriodPill options={PERIOD_OPTIONS} active={period} onChange={setPeriod} />
            </Box>

            {/* Compliance alert */}
            {!alertDismissed && <AlertStrip alert={complianceAlert} onDismiss={() => setAlertDismissed(true)} />}

            {/* KPI row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', cursor:'pointer' }, gap: '10px', marginBottom: '32px', width: '100%' }} onClick={handleActiveUsers}>
                <KPICard label="Active Users" value={activeUsers} sub={hasKpiData ? activeSubText : '—'} subType={activeSubType} placeholder={!hasKpiData} />                
                <KPICard label={`Searches (${period === '7d' ? '7 days' : period === '30d' ? '30 days' : 'All time'})`} value={searchCount} sub={matchRateNote} subType={matchRateType} note={widgetNote} placeholder={!hasKpiData} />
            </Box>

            {/* Quick actions */}
            <Typography sx={{ fontSize: tokens.font.size.xs, color: tokens.color.textSecondary, letterSpacing: tokens.font.letterSpacing.label, textTransform: 'uppercase', marginBottom: '10px', fontFamily: tokens.font.family }}>
                Quick actions
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '10px', width: '100%' }}>
                <ActionCard title="Add content" desc="Upload files or create a new module" onClick={handleAddContent} />
                <ActionCard title="Invite learners" desc="Invite users and assign roles" onClick={handleInviteLearners} />
            </Box>

        </Box>
    );
};

export default AdminDashboard;
